// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Bootstraps Embiggen: wires the drop zone, scale selector, worker, event log,
 * modals and delivery. Owns no heavy image logic — that lives in the worker.
 */

import * as Comlink from 'comlink';
import './styles/main.css';
import {
  buildShell,
  isOverlayOpen,
  modalContent,
  onModalClose,
  openModal,
  closeModal,
  renderResultStats,
  showProgress,
  toast,
  updateProgress,
  type Refs,
} from './ui';
import { mountEventDrawer, emit as logEmit } from './eventlog';
import { initGlossary } from './glossary';
import { mountCompare, type CompareHandle } from './compare';
import {
  canCopyImage,
  canShareFiles,
  canSaveFilePicker,
  copyImage,
  downloadBlob,
  saveBlob,
  shareFile,
} from './deliver';
import { outputFilename } from './format';
import type { EmbiggenWorker } from './worker';
import type { ProgressEvent, Scale, UpscaleResult } from './types';

const SCALE_KEY = 'embiggen.scale';

let refs: Refs;
let worker: Comlink.Remote<EmbiggenWorker> | null = null;

let currentFile: File | null = null;
let currentScale: Scale = 2;
let running = false;
let lastStage: string | null = null;

let lastResult: UpscaleResult | null = null;
let compareHandle: CompareHandle | null = null;
const liveUrls: string[] = [];
let previewUrl: string | null = null;
let detachDrawer: (() => void) | null = null;

function trackUrl(url: string): string {
  liveUrls.push(url);
  return url;
}

function revokeResultUrls(): void {
  for (const u of liveUrls.splice(0)) URL.revokeObjectURL(u);
}

function getWorker(): Comlink.Remote<EmbiggenWorker> {
  if (worker) return worker;
  const w = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  worker = Comlink.wrap<EmbiggenWorker>(w);
  worker.subscribe(Comlink.proxy(onProgress));
  return worker;
}

function onProgress(e: ProgressEvent): void {
  updateProgress(refs, e);
  if (e.stage !== lastStage) {
    lastStage = e.stage;
    if (e.stage === 'loading-model') logEmit('model', 'info', e.label ?? 'loading model');
    else if (e.stage === 'inferring') logEmit('model', 'info', e.label ?? 'running inference');
    else if (e.stage === 'encoding') logEmit('image', 'info', 'encoding PNG');
  }
}

// ── File intake ───────────────────────────────────────────────────────────────

async function loadFile(file: File): Promise<void> {
  if (!file.type.startsWith('image/')) {
    toast('That is not an image file.', 'err');
    logEmit('image', 'err', `rejected non-image: ${file.type || 'unknown type'}`);
    return;
  }
  clearResult();
  currentFile = file;

  if (previewUrl) URL.revokeObjectURL(previewUrl);
  previewUrl = URL.createObjectURL(file);
  refs.dzThumb.src = previewUrl;

  const dims = await imageDimensions(previewUrl).catch(() => null);
  const parts = [file.name, dims ? `${dims.w} × ${dims.h}` : null, humanSize(file.size)].filter(Boolean);
  refs.dzMeta.textContent = parts.join('  ·  ');

  refs.dzEmpty.hidden = true;
  refs.dzPreview.hidden = false;
  refs.upscaleBtn.disabled = false;
  refs.clearBtn.disabled = false;
  logEmit('image', 'ok', `loaded ${file.name}${dims ? ` (${dims.w}×${dims.h})` : ''}`);
}

async function loadSample(): Promise<void> {
  try {
    logEmit('image', 'info', 'loading sample image');
    const res = await fetch('samples/demo.jpg');
    if (!res.ok) throw new Error(`sample fetch ${res.status}`);
    const blob = await res.blob();
    // Route through the exact same path as a real upload.
    await loadFile(new File([blob], 'sample.jpg', { type: blob.type || 'image/jpeg' }));
  } catch (err) {
    toast('Could not load the sample.', 'err');
    logEmit('image', 'err', `sample failed: ${(err as Error)?.message ?? err}`);
  }
}

function imageDimensions(url: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => reject(new Error('decode failed'));
    img.src = url;
  });
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Run ───────────────────────────────────────────────────────────────────────

async function run(): Promise<void> {
  if (running || !currentFile) return;
  running = true;
  lastStage = null;
  refs.upscaleBtn.disabled = true;
  clearResult();
  showProgress(refs, true);
  logEmit('model', 'info', `upscaling ${currentScale}×`);

  try {
    const w = getWorker();
    const result = await w.process(currentFile, currentScale);
    lastResult = result;
    showProgress(refs, false);
    renderResult(result);
    logEmit(
      'model',
      'ok',
      `done: ${result.outWidth}×${result.outHeight} in ${(result.totalMs / 1000).toFixed(1)}s (${result.device})`,
    );
    toast('Upscaled — drag the slider to compare.');
  } catch (err) {
    showProgress(refs, false);
    const msg = (err as Error)?.message ?? String(err);
    logEmit('model', 'err', `failed: ${msg}`);
    toast(`Upscale failed: ${msg}`, 'err');
  } finally {
    running = false;
    refs.upscaleBtn.disabled = !currentFile;
  }
}

function renderResult(r: UpscaleResult): void {
  const beforeUrl = trackUrl(URL.createObjectURL(r.originalBlob));
  const afterUrl = trackUrl(URL.createObjectURL(r.blob));
  compareHandle?.destroy();
  compareHandle = mountCompare(refs.compareMount, beforeUrl, afterUrl, r.outWidth / r.outHeight);
  renderResultStats(refs, r);

  const shareFileList = [new File([r.blob], 'embiggen.png', { type: 'image/png' })];
  refs.saveBtn.hidden = !canSaveFilePicker();
  refs.copyBtn.hidden = !canCopyImage();
  refs.shareBtn.hidden = !canShareFiles(shareFileList);

  refs.result.hidden = false;
  refs.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function clearResult(): void {
  compareHandle?.destroy();
  compareHandle = null;
  revokeResultUrls();
  lastResult = null;
  refs.result.hidden = true;
  refs.compareMount.innerHTML = '';
}

function resetAll(): void {
  clearResult();
  currentFile = null;
  if (previewUrl) {
    URL.revokeObjectURL(previewUrl);
    previewUrl = null;
  }
  refs.fileInput.value = '';
  refs.dzThumb.removeAttribute('src');
  refs.dzPreview.hidden = true;
  refs.dzEmpty.hidden = false;
  refs.upscaleBtn.disabled = true;
  refs.clearBtn.disabled = true;
  showProgress(refs, false);
}

// ── Delivery ──────────────────────────────────────────────────────────────────

function currentOutName(): string {
  return outputFilename(currentFile?.name, lastResult?.scale ?? currentScale);
}

async function onDownload(): Promise<void> {
  if (!lastResult) return;
  downloadBlob(lastResult.blob, currentOutName());
  logEmit('export', 'ok', `downloaded ${currentOutName()}`);
}

async function onSave(): Promise<void> {
  if (!lastResult) return;
  const outcome = await saveBlob(lastResult.blob, currentOutName());
  if (outcome !== 'cancelled') logEmit('export', 'ok', `${outcome} ${currentOutName()}`);
}

async function onCopy(): Promise<void> {
  if (!lastResult) return;
  try {
    await copyImage(lastResult.blob);
    toast('Copied to clipboard.');
    logEmit('export', 'ok', 'copied image to clipboard');
  } catch (err) {
    toast('Copy failed — try Download instead.', 'err');
    logEmit('export', 'err', `copy failed: ${(err as Error)?.message ?? err}`);
  }
}

async function onShare(): Promise<void> {
  if (!lastResult) return;
  try {
    await shareFile(lastResult.blob, currentOutName(), 'Upscaled with Embiggen');
    logEmit('export', 'ok', 'shared image');
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') {
      toast('Share failed.', 'err');
      logEmit('export', 'err', `share failed: ${(err as Error)?.message ?? err}`);
    }
  }
}

// ── Drawer + modals ───────────────────────────────────────────────────────────

function openDrawer(): void {
  if (!refs.drawer.hidden) return;
  detachDrawer = mountEventDrawer(refs.drawer, closeDrawer);
  refs.drawer.hidden = false;
  refs.drawer.classList.add('open');
  refs.drawerToggle.setAttribute('aria-expanded', 'true');
}

function closeDrawer(): void {
  refs.drawer.classList.remove('open');
  refs.drawer.hidden = true;
  refs.drawerToggle.setAttribute('aria-expanded', 'false');
  detachDrawer?.();
  detachDrawer = null;
}

function openNamedModal(id: string): void {
  const { title, html } = modalContent(id);
  openModal(title, html);
}

// ── Wiring ────────────────────────────────────────────────────────────────────

function selectScale(scale: Scale): void {
  currentScale = scale;
  try {
    localStorage.setItem(SCALE_KEY, String(scale));
  } catch {
    /* private mode — ignore */
  }
  refs.scaleGrid.querySelectorAll<HTMLElement>('.scale-card').forEach((card) => {
    const s = Number(card.dataset.scale) as Scale;
    const active = s === scale;
    card.classList.toggle('active', active);
    card.setAttribute('aria-checked', String(active));
  });
}

function wire(): void {
  const dz = refs.dropzone;
  dz.addEventListener('click', () => refs.fileInput.click());
  dz.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      refs.fileInput.click();
    }
  });
  refs.fileInput.addEventListener('change', () => {
    const f = refs.fileInput.files?.[0];
    if (f) void loadFile(f);
  });
  refs.sampleBtn.addEventListener('click', (e) => {
    // Inside the dropzone, whose click opens the file picker — don't do both.
    e.stopPropagation();
    void loadSample();
  });
  dz.addEventListener('dragover', (e) => {
    e.preventDefault();
    dz.classList.add('dragover');
  });
  dz.addEventListener('dragleave', () => dz.classList.remove('dragover'));
  dz.addEventListener('drop', (e) => {
    e.preventDefault();
    dz.classList.remove('dragover');
    const f = e.dataTransfer?.files?.[0];
    if (f) void loadFile(f);
  });

  // Paste an image from the clipboard.
  window.addEventListener('paste', (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) {
          void loadFile(f);
          break;
        }
      }
    }
  });

  refs.scaleGrid.querySelectorAll<HTMLElement>('.scale-card').forEach((card) => {
    card.addEventListener('click', () => selectScale(Number(card.dataset.scale) as Scale));
  });

  refs.upscaleBtn.addEventListener('click', () => void run());
  refs.clearBtn.addEventListener('click', resetAll);
  refs.againBtn.addEventListener('click', resetAll);
  refs.downloadBtn.addEventListener('click', () => void onDownload());
  refs.saveBtn.addEventListener('click', () => void onSave());
  refs.copyBtn.addEventListener('click', () => void onCopy());
  refs.shareBtn.addEventListener('click', () => void onShare());

  refs.drawerToggle.addEventListener('click', () => {
    if (refs.drawer.hidden) openDrawer();
    else closeDrawer();
  });

  document.querySelectorAll<HTMLElement>('[data-modal]').forEach((el) => {
    el.addEventListener('click', () => openNamedModal(el.dataset.modal as string));
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (!refs.drawer.hidden) closeDrawer();
      else if (isOverlayOpen()) closeModal();
      return;
    }
    if (e.key === 'Enter' && !isOverlayOpen() && refs.drawer.hidden) {
      const el = document.activeElement;
      const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
      if (!typing && currentFile && !running) {
        e.preventDefault();
        void run();
      }
    }
  });

  onModalClose(() => {
    /* focus management hook — nothing extra needed today */
  });
}

// ── Boot ──────────────────────────────────────────────────────────────────────

function boot(): void {
  const root = document.getElementById('app');
  if (!root) return;
  refs = buildShell(root);
  initGlossary();
  wire();

  // Restore last-used scale.
  try {
    const saved = Number(localStorage.getItem(SCALE_KEY));
    if (saved === 2 || saved === 4) selectScale(saved);
  } catch {
    /* ignore */
  }

  logEmit('system', 'ok', 'ready — everything runs locally, nothing is uploaded');

  // Test hook for the automated dry-run: drive a synthetic image end-to-end.
  (window as unknown as { __embiggen?: unknown }).__embiggen = {
    loadFile: (f: File) => loadFile(f),
    run,
    getResult: () => lastResult,
    selectScale,
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
